import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  TextField,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import { Autocomplete } from "@material-ui/lab";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import AddIcon from "@material-ui/icons/Add";
import ValidationMessage from "../../../../../../components/validation/ValidationMessage";
import ColorSelectionIconButton from "../../../../../PrestoExpressEatprestoApp/pages/ThemeCustomizationPage/ColorSelectionIconButton";
import { CustomTheme } from "../../../../../../types/customTheme";
import TextfieldDefaultNew from "../../../../../../components/textField/TextfieldDefaultNew";
import ButtonCommon from "../../../../../../components/buttons/ButtonCommon";
import { useRouteMatch } from "react-router-dom";
import {
  createItemTypes,
  fetchItemTypesDetails,
} from "../../../../../../services/menuApp/categoryMenuService";

interface OverviewSectionProps {
  register: any;
  errors: any;
  open: boolean;
  setOpen: (open: boolean) => void;
  itemTypesList: any[];
  selectedItemType: any;
  onItemTypeChange: (event: any, value: any) => void;
  color: string;
  onColorChange: (color: string) => void;
  wrapperData: any;
  productData: any;
  classes: any;
  onFieldChange?: (field: string, value: any, isWrapperField: boolean) => void;
}

const OverviewSection: React.FC<OverviewSectionProps> = ({
  register,
  errors,
  open,
  setOpen,
  itemTypesList,
  selectedItemType,
  onItemTypeChange,
  color,
  onColorChange,
  wrapperData,
  productData,
  classes,
  onFieldChange,
}) => {
  const theme: CustomTheme = useTheme();
  const [itemTypeOptions, setItemTypeOptions] = useState<any[]>([]);
  const [isLoadingItemTypes, setIsLoadingItemTypes] = useState(false);
  const [isAddItemTypeModalOpen, setIsAddItemTypeModalOpen] = useState(false);
  const [newItemTypeName, setNewItemTypeName] = useState("");
  const [isAddingItemType, setIsAddingItemType] = useState(false);
  const [newItemTypeError, setNewItemTypeError] = useState("");
  const [localSelectedItemType, setLocalSelectedItemType] = useState<any>(null);

  const match: any = useRouteMatch();
  const locationId: string = match.params.locationId;

  // Define the "Add Item Type" option
  const ADD_ITEM_TYPE_OPTION = {
    id: "add-new",
    label: "+ Add Item Type",
    isAddButton: true,
  };

  // Fetch item types from API
  const fetchItemTypes = async () => {
    setIsLoadingItemTypes(true);
    try {
      const response = await fetchItemTypesDetails(locationId);
      console.log("Item types response:", response?.data?.data);
      if (response?.data?.data) {
        const formattedOptions = response.data.data.map((item: any) => ({
          id: item.id,
          label: item.type,
          name: item.type,
        }));
        console.log("Formatted options:", formattedOptions);
        setItemTypeOptions(formattedOptions);

        // After loading options, set the selected item type based on wrapperData
        if (wrapperData?.itemType) {
          const matchingItemType = formattedOptions.find(
            (item: any) =>
              item.id?.toString() === wrapperData.itemType?.toString(),
          );
          console.log("Found matching item type:", matchingItemType);

          if (matchingItemType) {
            setLocalSelectedItemType(matchingItemType);
            onItemTypeChange(null, matchingItemType);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching item types:", error);
    } finally {
      setIsLoadingItemTypes(false);
    }
  };

  // Create new item type
  const handleCreateItemType = async () => {
    if (!newItemTypeName.trim()) {
      setNewItemTypeError("Please enter item type name");
      return;
    }

    setIsAddingItemType(true);
    setNewItemTypeError("");

    try {
      const data = {
        type: newItemTypeName.trim(),
        locationId,
      };
      const response = await createItemTypes(locationId, data);

      console.log("Create item type response:", response?.data?.data);

      if (response?.data?.data) {
        const newItemType = {
          id: response.data.data.id,
          label: response.data.data.type,
          name: response.data.data.type,
        };

        // Add to options list
        setItemTypeOptions((prev) => [...prev, newItemType]);

        // Auto-select the newly created item type
        setLocalSelectedItemType(newItemType);
        onItemTypeChange(null, newItemType);
        if (onFieldChange) {
          onFieldChange("itemType", newItemType.id, true);
        }

        // Close modal and reset
        setIsAddItemTypeModalOpen(false);
        setNewItemTypeName("");
      }
    } catch (error) {
      console.error("Error creating item type:", error);
      setNewItemTypeError("Failed to create item type. Please try again.");
    } finally {
      setIsAddingItemType(false);
    }
  };

  // Initialize selected item type from wrapperData when component mounts or wrapperData changes
  useEffect(() => {
    if (wrapperData?.itemType && itemTypeOptions.length > 0) {
      const matchingItemType = itemTypeOptions.find(
        (item: any) =>
          item.id === wrapperData.itemType ||
          item.label === wrapperData.itemType,
      );
      if (matchingItemType && !localSelectedItemType) {
        console.log(
          "Setting selected item type from wrapperData:",
          matchingItemType,
        );
        setLocalSelectedItemType(matchingItemType);
        onItemTypeChange(null, matchingItemType);
      }
    }
  }, [wrapperData, itemTypeOptions]);

  // Load item types when component mounts
  useEffect(() => {
    fetchItemTypes();
  }, []);

  // Sync with prop selectedItemType
  useEffect(() => {
    if (selectedItemType && selectedItemType.id !== localSelectedItemType?.id) {
      setLocalSelectedItemType(selectedItemType);
    }
  }, [selectedItemType]);

  // Combine options with the "Add Item Type" option at the end
  const displayOptions = [...itemTypeOptions, ADD_ITEM_TYPE_OPTION];

  console.log("Display options:", displayOptions);
  console.log("Selected item type:", localSelectedItemType);
  console.log("Wrapper data itemType:", wrapperData?.itemType);

  const handleAddItemTypeClick = () => {
    console.log("Add Item Type clicked - opening modal");
    setIsAddItemTypeModalOpen(true);
  };

  return (
    <>
      <Paper
        elevation={0}
        style={{
          marginBottom: "24px",
          borderRadius: "16px",
          backgroundColor: theme.palette.background.menuProductColor,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Box style={{ padding: "20px 24px" }}>
          <Box display="flex" alignItems="center" mb={1}>
            <Box
              style={{
                backgroundColor: "#5e35b1",
                borderRadius: "8px",
                padding: "6px",
                marginRight: "12px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Icon
                icon="lucide:layout-dashboard"
                width="20"
                style={{ color: "white" }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                style={{ fontWeight: "bold" }}
                align="left"
              >
                Overview
              </Typography>
              <Typography variant="caption" align="left">
                Basic information about this menu wrapper.
              </Typography>
            </Box>
          </Box>

          <Box mt={4}>
            <Grid container spacing={4}>
              {/* LEFT COLUMN */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  {/* Name - Product Wrapper Field */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      style={{ marginBottom: "8px", fontWeight: 500 }}
                      align="left"
                    >
                      Name <span style={{ color: "#f44336" }}>*</span>
                    </Typography>
                    <TextField
                      id="name"
                      name="name"
                      inputRef={register({ required: "Name is required" })}
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.textField}
                      onChange={(e) =>
                        onFieldChange?.("name", e.target.value, true)
                      }
                    />
                    {errors.name && (
                      <ValidationMessage message={errors.name.message} />
                    )}
                  </Grid>

                  {/* Item Type - Product Wrapper Field with Add Option */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      style={{ marginBottom: "8px", fontWeight: 500 }}
                      align="left"
                    >
                      Item Type <span style={{ color: "#f44336" }}>*</span>
                    </Typography>
                    <Autocomplete
                      open={open}
                      onOpen={() => setOpen(true)}
                      onClose={() => setOpen(false)}
                      size="small"
                      options={displayOptions}
                      loading={isLoadingItemTypes}
                      value={localSelectedItemType}
                      getOptionLabel={(option: any) => {
                        if (!option) return "";
                        if (option.id === "add-new") return "";
                        return option.label || option.name || "";
                      }}
                      disableClearable
                      onChange={(event, value) => {
                        // Don't trigger change for the add button
                        if (value?.id === "add-new") {
                          return;
                        }
                        setLocalSelectedItemType(value);
                        onItemTypeChange(event, value);
                        if (onFieldChange) {
                          onFieldChange("itemType", value?.id || value, true);
                        }
                      }}
                      classes={{ root: classes.autoComplete }}
                      popupIcon={<ArrowDropDownIcon color="inherit" />}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          color="inherit"
                          placeholder="Select item type"
                        />
                      )}
                      renderOption={(option) => {
                        // Check if this is the "Add Item Type" option
                        if (option && option.id === "add-new") {
                          return (
                            <Box
                              display="flex"
                              alignItems="center"
                              style={{
                                cursor: "pointer",
                                color: "#5e35b1",
                                padding: "8px 16px",
                                width: "100%",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddItemTypeClick();
                              }}
                            >
                              <AddIcon
                                style={{ fontSize: "16px", marginRight: "8px" }}
                              />
                              <Typography variant="body2">
                                + Add Item Type
                              </Typography>
                            </Box>
                          );
                        }
                        // Return regular item type option
                        return (
                          <Typography
                            variant="body2"
                            style={{ padding: "8px 16px", width: "100%" }}
                          >
                            {option?.label || option?.name}
                          </Typography>
                        );
                      }}
                    />
                  </Grid>

                  {/* Short Name - Product Wrapper Field (Optional) */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      style={{ marginBottom: "8px", fontWeight: 500 }}
                      align="left"
                    >
                      Short Name
                    </Typography>
                    <TextField
                      id="nameShort"
                      name="nameShort"
                      inputRef={register()}
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.textField}
                      onChange={(e) =>
                        onFieldChange?.("nameShort", e.target.value, true)
                      }
                    />
                  </Grid>

                  {/* Colour Selection - Product Wrapper Field */}
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      style={{ marginBottom: "8px", fontWeight: 500 }}
                      align="left"
                    >
                      Colour <span style={{ color: "#f44336" }}>*</span>
                    </Typography>
                    <ColorSelectionIconButton
                      title="Color"
                      colorCode={color}
                      setColor={(colorVal: any) => {
                        onColorChange(colorVal.hex);
                        if (onFieldChange) {
                          onFieldChange("colour", colorVal.hex, true);
                        }
                      }}
                      setInitialData={() =>
                        onColorChange(wrapperData?.colour || "#FFBA00")
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* RIGHT COLUMN: Description - Product Field */}
              <Grid item xs={12} md={4}>
                <Typography
                  variant="body2"
                  style={{ marginBottom: "8px", fontWeight: 500 }}
                  align="left"
                >
                  Description
                </Typography>
                <TextField
                  id="desc"
                  name="desc"
                  multiline
                  rows={5}
                  inputRef={register()}
                  variant="outlined"
                  className={classes.textField}
                  fullWidth
                  onChange={(e) =>
                    onFieldChange?.("desc", e.target.value, false)
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>

      {/* Add Item Type Modal */}
      <Dialog
        open={isAddItemTypeModalOpen}
        onClose={() => {
          console.log("Modal closing");
          setIsAddItemTypeModalOpen(false);
          setNewItemTypeName("");
          setNewItemTypeError("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            Add New Item Type
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: "8px" }}>
            <Grid item xs={12}>
              <Typography style={{ paddingLeft: "4px", marginBottom: "8px" }}>
                Item Type Name <span style={{ color: "#f44336" }}>*</span>
              </Typography>
              <TextfieldDefaultNew
                id="new-item-type"
                name="new-item-type"
                onChange={(e: any) => {
                  setNewItemTypeError("");
                  setNewItemTypeName(e.target.value);
                }}
                value={newItemTypeName}
                type="text"
              />
              {newItemTypeError && (
                <Typography
                  variant="caption"
                  style={{
                    color: "#f44336",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {newItemTypeError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
          <ButtonCommon
            variant="contained"
            style={{ fontSize: 11, width: "100px" }}
            color="yellow"
            onClick={() => {
              setIsAddItemTypeModalOpen(false);
              setNewItemTypeName("");
              setNewItemTypeError("");
            }}
          >
            Cancel
          </ButtonCommon>
          <ButtonCommon
            variant="contained"
            style={{ fontSize: 11, width: "100px" }}
            isLoadingPage={isAddingItemType}
            color="green"
            onClick={handleCreateItemType}
          >
            Create
          </ButtonCommon>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OverviewSection;
